import React from 'react';
import {
  DriverEmergencyContact,
  RentalTrackingDetail,
} from '@/services/api/rentTracking';
import {
  toMapsPinUrl,
  toMapsSearchUrl,
  toTelUrl,
  toWhatsAppUrl,
} from '@/utils/contact';
import { formatDateTime, formatRupiah } from '@/utils/format';
import {
  chargeTypeLabel,
  formatAddressParts,
  formatChargeRange,
} from '../rentTrackingDisplay';

/** Label above, value below — the shape every block here repeats. */
const Row: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="grid grid-cols-[92px_1fr] gap-2 py-1">
    <span className="text-[12px] text-gray-500">{label}</span>
    <div className="text-[13px] text-gray-900">{children}</div>
  </div>
);

const Band: React.FC<{ children: string }> = ({ children }) => (
  <p className="mt-3 mb-1 text-[11px] font-semibold tracking-wide text-gray-500 uppercase">
    {children}
  </p>
);

const ExternalLink: React.FC<{ href: string; children: React.ReactNode }> = ({
  href,
  children,
}) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="text-primary hover:underline"
  >
    {children}
  </a>
);

/** A number ops can dial or WhatsApp, with both links beside it. */
const PhoneLine: React.FC<{ phone: string }> = ({ phone }) => {
  const tel = toTelUrl(phone);
  const wa = toWhatsAppUrl(phone);
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      {tel ? (
        <ExternalLink href={tel}>{phone}</ExternalLink>
      ) : (
        <span>{phone}</span>
      )}
      {wa && (
        <>
          <span className="text-gray-300">·</span>
          <ExternalLink href={wa}>WhatsApp</ExternalLink>
        </>
      )}
    </span>
  );
};

/** A written address, with a Maps search when there is anything to search. */
const AddressLine: React.FC<{ address: string; city?: string | null }> = ({
  address,
  city,
}) => {
  const maps = toMapsSearchUrl(address, city);
  return (
    <span>
      {address}
      {city && !address.toLowerCase().includes(city.toLowerCase())
        ? `, ${city}`
        : ''}
      {maps && (
        <>
          {' '}
          <ExternalLink href={maps}>
            <i className="ri-map-pin-line align-middle"></i> Maps
          </ExternalLink>
        </>
      )}
    </span>
  );
};

export const UnitDetailBlocks: React.FC<{ detail: RentalTrackingDetail }> = ({
  detail,
}) => {
  const { rental, vehicle, arrears, driver, repossession } = detail;
  const driverAddress = formatAddressParts(driver.address);
  const pin = toMapsPinUrl(
    vehicle.position?.latitude,
    vehicle.position?.longitude
  );

  return (
    <div>
      <Band>Motor</Band>
      <Row label="Unit">
        <span className="font-medium">{vehicle.platNomor ?? '-'}</span>
        {vehicle.modelName || vehicle.oemName ? (
          <span className="text-gray-500">
            {' '}
            · {[vehicle.oemName, vehicle.modelName].filter(Boolean).join(' ')}
          </span>
        ) : null}
        {vehicle.vin && (
          <p className="text-[12px] text-gray-500 font-mono">
            VIN {vehicle.vin}
          </p>
        )}
      </Row>
      <Row label="Posisi">
        {vehicle.position ? (
          <>
            {pin ? (
              <ExternalLink href={pin}>
                {vehicle.position.latitude.toFixed(5)},{' '}
                {vehicle.position.longitude.toFixed(5)}
              </ExternalLink>
            ) : (
              '-'
            )}
            <p className="text-[12px] text-gray-500">
              {vehicle.position.lastAt
                ? formatDateTime(vehicle.position.lastAt)
                : '-'}
              {vehicle.position.ignition === null
                ? ''
                : vehicle.position.ignition
                  ? ' · menyala'
                  : ' · mati'}
              {vehicle.position.odometer
                ? ` · ${vehicle.position.odometer} km`
                : ''}
              {vehicle.position.bms1Soc
                ? ` · SOC ${vehicle.position.bms1Soc}%`
                : ''}
            </p>
          </>
        ) : (
          <span className="text-gray-500">Belum pernah kirim posisi</span>
        )}
      </Row>
      <Row label="Bayaran harian">
        {formatRupiah(rental.dailyPrice)}
        {rental.contractType ? (
          <span className="text-gray-500"> · {rental.contractType}</span>
        ) : null}
      </Row>

      <Band>Informasi rental</Band>
      <Row label="Kode rental">{rental.code}</Row>
      <Row label="Menunggak">
        <span className="font-semibold">
          {arrears.days} hari · {formatRupiah(arrears.amount)}
        </span>
        {arrears.breakdown.length > 0 && (
          <p className="text-[12px] text-gray-500">
            {arrears.breakdown
              .map(
                (item) =>
                  `${chargeTypeLabel(item.type)} ${formatRupiah(item.amount)}`
              )
              .join(' · ')}
          </p>
        )}
      </Row>
      <Row label="Rentang">
        {formatChargeRange(arrears.oldestChargeDate, arrears.newestChargeDate)}
      </Row>
      <Row label="Handover">
        {rental.handoverDate ? formatDateTime(rental.handoverDate) : '-'}
        {rental.dailyCutoffTime && (
          <p className="text-[12px] text-gray-500">
            batas harian pukul {rental.dailyCutoffTime}
          </p>
        )}
      </Row>
      <Row label="Saldo prabayar">
        sewa {formatRupiah(arrears.prepaid.rent)} · cicilan{' '}
        {formatRupiah(arrears.prepaid.installment)}
      </Row>
      {repossession && (
        <Row label="Case repo">
          <span className="font-medium">{repossession.code}</span>
          <span className="text-gray-500">
            {' '}
            · {repossession.status} · {repossession.severity}
          </span>
          <p className="text-[12px] text-gray-500">
            {repossession.assignedOpsTeam
              ? `Tim ${repossession.assignedOpsTeam}`
              : 'Belum di-assign'}{' '}
            · dibuat {formatDateTime(repossession.createdAt)}
          </p>
        </Row>
      )}

      <Band>Informasi mitra</Band>
      <Row label="Nama">
        {driver.name ?? '-'}
        <span className="text-gray-500">
          {driver.code ? ` · ${driver.code}` : ''} (id {driver.ID})
        </span>
      </Row>
      <Row label="Telepon">
        {driver.phoneNumber ? (
          <PhoneLine phone={driver.phoneNumber} />
        ) : (
          <span className="text-gray-500">-</span>
        )}
      </Row>
      <Row label="Alamat">
        {driverAddress ? (
          <AddressLine address={driverAddress} />
        ) : (
          <span className="text-gray-500">-</span>
        )}
      </Row>
      {(rental.hubName || rental.hubCityName) && (
        <Row label="Hub">
          {[rental.hubName, rental.hubCityName].filter(Boolean).join(' · ')}
        </Row>
      )}

      <Band>Kontak darurat</Band>
      {driver.contactStatus === 'ERROR' ? (
        <p className="rounded-lg bg-amber-50 px-3 py-2 text-[12px] text-amber-800">
          Gagal memuat kontak dari driver-service ({driver.contactError}). Coba
          muat ulang panel ini.
        </p>
      ) : driver.contactStatus === 'NOT_FOUND' ? (
        <p className="rounded-lg bg-gray-50 px-3 py-2 text-[12px] text-gray-600">
          Mitra id {driver.ID} tidak ada di driver-service, jadi telepon,
          alamat, dan kontak daruratnya tidak bisa ditarik. Nama dan kodenya
          diambil dari data rental.
        </p>
      ) : driver.emergencyContacts.length === 0 ? (
        <p className="text-[13px] text-gray-500">
          Tidak ada kontak darurat pada data KYC mitra.
        </p>
      ) : (
        <ul className="space-y-2">
          {driver.emergencyContacts.map((contact, index) => (
            <EmergencyContactRow
              key={`${contact.phoneNumber}-${index}`}
              contact={contact}
            />
          ))}
        </ul>
      )}
      {driver.contactFetchedAt && (
        <p className="mt-2 text-[11px] text-gray-400">
          Sumber kontak: driver-service, ditarik{' '}
          {formatDateTime(driver.contactFetchedAt)}
        </p>
      )}
    </div>
  );
};

const EmergencyContactRow: React.FC<{ contact: DriverEmergencyContact }> = ({
  contact,
}) => (
  <li className="rounded-lg border border-gray-100 px-3 py-2">
    <p className="text-[13px] text-gray-900">
      {contact.name}
      <span className="text-gray-500"> ({contact.relationship})</span>
    </p>
    <p className="text-[13px]">
      <PhoneLine phone={contact.phoneNumber} />
    </p>
    {contact.address && (
      <p className="text-[12px] text-gray-500">
        <AddressLine address={contact.address} city={contact.cityName} />
      </p>
    )}
  </li>
);

export default UnitDetailBlocks;
